import { useState, useEffect } from "react";

// Props:
//   data   : { phase_id, question, cards, vote_seconds, already_voted, my_vote } | null
//   result : { winner, question, label } | null
//   final  : { story } | null
//   onVote : (card) => void

const COLOR = "#FF6B35";

export default function ScreenplayPlayer({ data, result, final, onVote }) {
  const [selected, setSelected] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);

  // Сбрасываем выбор при новой фазе
  useEffect(() => {
    if (data) {
      setSelected(data.my_vote || null);
      setTimeLeft(data.vote_seconds || 25);
    }
  }, [data?.phase_id]);

  // Таймер
  useEffect(() => {
    if (!data || selected) return;
    if (timeLeft <= 0) return;
    const t = setTimeout(() => setTimeLeft(v => Math.max(v - 1, 0)), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, data, selected]);

  const handleVote = (card) => {
    if (selected || data?.already_voted) return;
    setSelected(card);
    onVote(card);
  };

  // ── Финал ────────────────────────────────────────────────────────────────
  if (final) {
    return (
      <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
        <div style={{ fontSize: "3rem", marginBottom: "0.75rem" }}>📖</div>
        <div style={{
          fontFamily: "'Fredoka One',sans-serif",
          fontSize: "1.4rem",
          color: COLOR,
          marginBottom: "0.6rem",
        }}>История готова!</div>
        <div style={{
          fontFamily: "'Nunito',sans-serif",
          fontSize: "0.9rem",
          color: "#888",
          lineHeight: 1.6,
        }}>Смотри на экран хоста —{"\n"}история озвучивается</div>

        <div style={{
          marginTop: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
        }}>
          {final.story.map((part, i) => (
            <div key={i} style={{
              background: "#ffffff08",
              border: "1px solid #ffffff12",
              borderRadius: "0.75rem",
              padding: "0.6rem 1rem",
              display: "flex",
              gap: "0.6rem",
              textAlign: "left",
            }}>
              <span style={{
                fontFamily: "'Nunito',sans-serif",
                fontSize: "0.7rem",
                color: "#666",
                minWidth: 60,
                textTransform: "uppercase",
                letterSpacing: ".05em",
                flexShrink: 0,
                paddingTop: 2,
              }}>{part.label}</span>
              <span style={{
                fontFamily: "'Fredoka One',sans-serif",
                fontSize: "0.95rem",
                color: "#fff",
                lineHeight: 1.25,
              }}>{part.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Результат фазы ───────────────────────────────────────────────────────
  if (result) {
    const isWinner = selected === result.winner;
    return (
      <div style={{ textAlign: "center", padding: "1rem 0" }}>
        <div style={{
          fontFamily: "'Nunito',sans-serif",
          fontSize: "0.75rem",
          color: "#888",
          marginBottom: "0.5rem",
          textTransform: "uppercase",
          letterSpacing: ".08em",
        }}>{result.label}</div>
        <div style={{
          fontFamily: "'Fredoka One',sans-serif",
          fontSize: "1rem",
          color: "#ccc",
          marginBottom: "1rem",
        }}>{result.question}</div>

        <div style={{
          background: `${COLOR}18`,
          border: `2.5px solid ${COLOR}`,
          borderRadius: "1.25rem",
          padding: "1rem 1.5rem",
          marginBottom: "1rem",
        }}>
          <div style={{
            fontFamily: "'Fredoka One',sans-serif",
            fontSize: "1.3rem",
            color: "#fff",
          }}>{result.winner}</div>
          <div style={{
            fontFamily: "'Nunito',sans-serif",
            fontSize: "0.8rem",
            color: COLOR,
            marginTop: "0.3rem",
          }}>🏆 Победитель фазы</div>
        </div>

        {selected && (
          <div style={{
            fontFamily: "'Nunito',sans-serif",
            fontSize: "0.85rem",
            color: isWinner ? "#3dffa0" : "#888",
          }}>
            {isWinner ? "✅ Ты угадал — +100 очков!" : `Ты выбрал: "${selected}"`}
          </div>
        )}
      </div>
    );
  }

  // ── Выбор карточки ───────────────────────────────────────────────────────
  if (!data) {
    return (
      <div style={{ textAlign: "center", padding: "2rem 0", color: "#666" }}>
        <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>⏳</div>
        <div style={{ fontFamily: "'Fredoka One',sans-serif", fontSize: "1rem" }}>
          Ждём начала...
        </div>
      </div>
    );
  }

  const voted = !!(selected || data.already_voted);
  const progress = timeLeft / (data.vote_seconds || 25);

  return (
    <div style={{ padding: "0.25rem 0" }}>
      {/* Вопрос */}
      <div style={{
        background: "#1a0a00",
        border: `2px solid ${COLOR}44`,
        borderRadius: "1rem",
        padding: "0.85rem 1rem",
        marginBottom: "1rem",
        textAlign: "center",
      }}>
        <div style={{
          fontFamily: "'Nunito',sans-serif",
          fontSize: "0.7rem",
          color: "#888",
          marginBottom: "0.3rem",
          textTransform: "uppercase",
          letterSpacing: ".08em",
        }}>📝 Выбери вариант</div>
        <div style={{
          fontFamily: "'Fredoka One',sans-serif",
          fontSize: "1.15rem",
          color: "#fff",
          lineHeight: 1.25,
        }}>{data.question}</div>
      </div>

      {/* Таймер */}
      {!voted && (
        <div style={{ marginBottom: "0.85rem" }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "0.3rem",
          }}>
            <span style={{
              fontFamily: "'Nunito',sans-serif",
              fontSize: "0.75rem",
              color: "#888",
            }}>Осталось</span>
            <span style={{
              fontFamily: "'Fredoka One',sans-serif",
              fontSize: "0.9rem",
              color: timeLeft < 8 ? "#ff3cac" : COLOR,
            }}>{timeLeft}с</span>
          </div>
          <div style={{ height: 4, background: "#ffffff12", borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${Math.round(progress * 100)}%`,
              background: timeLeft < 8 ? "#ff3cac" : COLOR,
              borderRadius: 2,
              transition: "width 1s linear",
            }} />
          </div>
        </div>
      )}

      {/* Карточки */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        {(data.cards || []).map((card, i) => {
          const isSelected = selected === card || data.my_vote === card;
          return (
            <button
              key={i}
              onClick={() => handleVote(card)}
              disabled={voted}
              style={{
                width: "100%",
                padding: "0.9rem 1rem",
                borderRadius: "0.85rem",
                border: `2.5px solid ${isSelected ? COLOR : "#ffffff18"}`,
                background: isSelected ? `${COLOR}22` : "#ffffff08",
                color: isSelected ? "#fff" : "#ccc",
                fontFamily: "'Fredoka One',sans-serif",
                fontSize: "1rem",
                textAlign: "left",
                cursor: voted ? "default" : "pointer",
                boxShadow: isSelected ? `3px 3px 0 #000` : "none",
                transform: isSelected ? "translate(-1px,-1px)" : "none",
                transition: "all .12s",
                WebkitTapHighlightColor: "transparent",
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
              }}
            >
              <span style={{
                width: 26, height: 26,
                borderRadius: "0.4rem",
                background: isSelected ? COLOR : "#ffffff15",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'Fredoka One',sans-serif",
                fontSize: "0.8rem",
                color: isSelected ? "#000" : "#888",
                flexShrink: 0,
              }}>
                {["A","B","C","D"][i] || i + 1}
              </span>
              {card}
            </button>
          );
        })}
      </div>

      {voted && !result && (
        <div style={{
          textAlign: "center",
          marginTop: "1rem",
          fontFamily: "'Nunito',sans-serif",
          fontSize: "0.85rem",
          color: "#3dffa0",
        }}>
          ✅ Ответ принят — ждём остальных
        </div>
      )}
    </div>
  );
}